import { UsageLocalStorageInterface } from "../ReportTypes";
import { parse, isValid } from "date-fns";
import { Tooltip } from "../../../shared/Tooltip";
import DatePicker from "react-datepicker";

const UsageDateSelector = ({
  stateData,
  setStateData,
  yesterday,
  children,
}: {
  stateData: UsageLocalStorageInterface;
  setStateData: React.Dispatch<
    React.SetStateAction<UsageLocalStorageInterface | null>
  >;
  yesterday: Date;
  children?: React.ReactNode;
}) => {
  const ONE_YEAR = 31536000000;

  return (
    <div className="flex justify-between">
      <div className="inline-flex">
        <Tooltip
          options={{ aligned: "center", raise: "mb-10" }}
          message="1 year maximum date range."
        >
          <DatePicker
            id="report-range"
            selected={stateData.startDate}
            onChange={(dates) => {
              if (dates) {
                const [start, end] = dates;
                if (
                  end !== null &&
                  start !== null &&
                  end.getTime() - start.getTime() > ONE_YEAR
                ) {
                  setStateData({
                    ...stateData,
                    dateRange: "custom",
                  });
                } else {
                  setStateData({
                    ...stateData,
                    startDate: start || undefined,
                    endDate: end || undefined,
                    dateRange: "custom",
                  });
                }
              }
            }}
            onChangeRaw={(event) => {
              const userEnteredDate = event.target.value;
              if (typeof userEnteredDate !== "undefined") {
                const datesArray = userEnteredDate.split(" - ");
                if (datesArray.length === 2) {
                  const startDate = parse(
                    datesArray[0],
                    "MM/dd/yyyy",
                    new Date()
                  );
                  const endDate = parse(
                    datesArray[1],
                    "MM/dd/yyyy",
                    new Date()
                  );
                  if (
                    isValid(startDate) &&
                    isValid(endDate) &&
                    endDate.getTime() - startDate.getTime() > ONE_YEAR
                  ) {
                    setStateData({
                      ...stateData,
                      endDate: startDate,
                    });
                  } else {
                    if (isValid(startDate)) {
                      setStateData({
                        ...stateData,
                        startDate: startDate,
                        dateRange: "custom",
                      });
                    }
                    if (isValid(endDate)) {
                      setStateData({
                        ...stateData,
                        endDate: endDate,
                        dateRange: "custom",
                      });
                    }
                  }
                }
              }
            }}
            startDate={stateData.startDate}
            endDate={stateData.endDate}
            maxDate={yesterday}
            selectsRange
            nextMonthButtonLabel=">"
            previousMonthButtonLabel="<"
            className="!w-48"
            popperPlacement="bottom"
            popperModifiers={[
              {
                name: "flip",
                options: {
                  flipVariations: false,
                  allowedAutoPlacements: ["bottom", "bottom-start"],
                  fallbackPlacements: ["bottom", "bottom-start"],
                },
              },
              {
                name: "preventOverflow",
                enabled: false,
              },
              {
                name: "hide",
                enabled: false,
              },
            ]}
          />
        </Tooltip>
        <div className="ml-4 !w-36">
          <select
            onChange={(event) => {
              setStateData({
                ...stateData,
                dateRange: event.target
                  .value as UsageLocalStorageInterface["dateRange"],
              });
            }}
            value={stateData.dateRange}
            className="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:border-dm-light-blue focus:outline-none focus:ring-1 focus:ring-dm-light-blue sm:text-sm"
          >
            <option value={"year-to-date"}>School YTD</option>
            <option value={"last-week"}>Last 7 days</option>
            <option value={"thirty-days"}>Last 30 days</option>
            <option value={"custom"}>Custom range</option>
          </select>
        </div>
      </div>

      {children}
    </div>
  );
};

export default UsageDateSelector;
